import { useMainStore } from '@/stores/main'
import { storeId } from '@/stores/storeId'
import { defineStore } from 'pinia'

import Sunny16CadyAccountService from '@/services/Sunny16CadyAccountService'

import type { SelectedStudent, StudentSource } from '@/components/CheckoutPay/types'
import { StudentFlowState } from '@/components/CheckoutPay/types'

import type {
  CadyAccountPrincipal,
  LoginRequest,
  RegisterRequest
} from '@VelocitySquared/cady-sunny16-commerce-integration-models'

export enum AccountFormTab {
  CREATE = 'create',
  LOGIN = 'login'
}
export enum AccountFormState {
  FORM = 'form',
  SUCCESS = 'success',
  RESET_PASSWORD = 'reset-password',
  INITIAL_CREATED = 'initial-created',
  VERIFIED_EMAIL = 'verified-email'
}
export enum LoginAlertType {
  NONE = 'none',
  WARNING = 'warning',
  ERROR = 'error'
}

interface CreateAccountFields {
  firstName: string
  lastName: string
  email: string
  mobileNumber: string
  password: string
  confirmPassword: string
}

interface LoginFields {
  emailOrUsername: string
  password: string
  rememberMe: boolean
}

interface ResetPasswordFields {
  email: string
  code: string
  newPassword: string
  confirmPassword: string
}

interface UserInfo {
  email: string
  id: string
  legacyCmsUserId: number
  legacyAccountId: number
  name: string
}

interface CheckoutStoreState {
  /** Which tab is active: create account or login */
  accountFormTab: AccountFormTab
  /** Current view state of the account form */
  accountFormState: AccountFormState
  /** Type of login alert to display */
  loginAlertType: LoginAlertType
  /** Create account form field values */
  createAccount: CreateAccountFields
  /** Login form field values */
  login: LoginFields
  /** Reset password form field values */
  resetPassword: ResetPasswordFields
  /** Whether the user is authenticated (for later integration) */
  isAuthenticated: boolean
  /** Authenticated user identity populated after login or registration. */
  userInfo: UserInfo | null
  /** Student assignment flow state (persisted for page refresh) */
  studentFlowState: StudentFlowState
  /** The assigned student (persisted for page refresh) */
  selectedStudent: SelectedStudent | null
  /** How the student was added — determines edit behavior */
  studentSource: StudentSource | null
  /** Selected relationship to student */
  relationship: string
  /** Whether terms were accepted */
  termsAgreed: boolean
}

function defaultCreateAccountFields(): CreateAccountFields {
  return {
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: ''
  }
}

function defaultLoginFields(): LoginFields {
  return {
    emailOrUsername: '',
    password: '',
    rememberMe: false
  }
}

function defaultResetPasswordFields(): ResetPasswordFields {
  return {
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  }
}

function buildUserInfo(principal: CadyAccountPrincipal | null | undefined): UserInfo {
  return {
    email: principal?.Email ?? '',
    id: principal?.Id ?? '',
    legacyCmsUserId: principal?.LegacyCmsUserId ?? 0,
    legacyAccountId: principal?.LegacyAccountId ?? 0,
    name: principal?.Name ?? ''
  }
}

export const useCheckoutStore = defineStore(storeId.CHECKOUT, {
  state: (): CheckoutStoreState => ({
    accountFormTab: AccountFormTab.CREATE,
    accountFormState: AccountFormState.FORM,
    loginAlertType: LoginAlertType.NONE,
    createAccount: defaultCreateAccountFields(),
    login: defaultLoginFields(),
    resetPassword: defaultResetPasswordFields(),
    isAuthenticated: false,
    userInfo: null,
    studentFlowState: StudentFlowState.SEARCHING,
    selectedStudent: null,
    studentSource: null,
    relationship: '',
    termsAgreed: false
  }),
  getters: {},
  actions: {
    // --- Tab / view state ---
    /** Switch tabs and reset form state and alerts for a clean view. */
    setAccountFormTab(tab: AccountFormTab) {
      this.accountFormTab = tab
      this.accountFormState = AccountFormState.FORM
      this.loginAlertType = LoginAlertType.NONE
    },
    setAccountFormState(state: AccountFormState) {
      this.accountFormState = state
    },
    setLoginAlertType(type: LoginAlertType) {
      this.loginAlertType = type
    },
    // --- Create account fields ---
    setCreateAccountField<K extends keyof CreateAccountFields>(
      field: K,
      value: CreateAccountFields[K]
    ) {
      this.createAccount[field] = value
    },
    resetCreateAccountFields() {
      this.createAccount = defaultCreateAccountFields()
    },
    // --- Login fields ---
    setLoginField<K extends keyof LoginFields>(field: K, value: LoginFields[K]) {
      this.login[field] = value
    },
    resetLoginFields() {
      this.login = defaultLoginFields()
    },
    // --- Reset password fields ---
    setResetPasswordEmail(email: string) {
      this.resetPassword.email = email
    },
    setResetPasswordCode(code: string) {
      this.resetPassword.code = code
    },
    setResetPasswordNewPassword(value: string) {
      this.resetPassword.newPassword = value
    },
    setResetPasswordConfirmPassword(value: string) {
      this.resetPassword.confirmPassword = value
    },
    resetResetPasswordFields() {
      this.resetPassword = defaultResetPasswordFields()
    },
    // --- Composite actions ---
    /**
     * Reset all account form fields and view state to defaults.
     */
    resetAccountForms() {
      this.createAccount = defaultCreateAccountFields()
      this.login = defaultLoginFields()
      this.resetPassword = defaultResetPasswordFields()
      this.loginAlertType = LoginAlertType.NONE
      this.accountFormState = AccountFormState.FORM
      this.userInfo = null
    },
    /**
     * Wipe in-progress account form input while preserving completed
     * authentication. Used when the user takes an action that interrupts
     * the form workflow mid-typing (e.g. clicks the Basic→Simple upgrade
     * box on Checkout Summary and is routed to Scene Selection — the
     * round-trip back lands on a fresh form, not stale typed values).
     *
     * - **Always wipes:** `createAccount`, `login`, `resetPassword`
     *   field objects, `loginAlertType`.
     * - **Wipes only when not authenticated:** `accountFormTab` (back to
     *   `CREATE`), `accountFormState` (back to `FORM`).
     * - **Never touches:** `isAuthenticated`, `userInfo`, downstream
     *   student-flow / terms state. An already-authenticated user keeps
     *   their verified-state view (`INITIAL_CREATED` / `VERIFIED_EMAIL`).
     */
    clearInProgressAccountForm() {
      this.createAccount = defaultCreateAccountFields()
      this.login = defaultLoginFields()
      this.resetPassword = defaultResetPasswordFields()
      this.loginAlertType = LoginAlertType.NONE
      if (!this.isAuthenticated) {
        this.accountFormTab = AccountFormTab.CREATE
        this.accountFormState = AccountFormState.FORM
      }
    },
    async submitRegister(): Promise<{
      success: boolean
      message?: string
      switched?: boolean
    }> {
      const service = new Sunny16CadyAccountService()
      const digits = this.createAccount.mobileNumber.replace(/\D/g, '')
      const phoneE164 = digits.length === 10 ? `+1${digits}` : `+${digits}`
      const request: RegisterRequest = {
        Email: this.createAccount.email,
        Password: this.createAccount.password,
        GivenName: this.createAccount.firstName,
        FamilyName: this.createAccount.lastName,
        Method: 'password',
        PhoneNumber: phoneE164,
        AutoLogin: true
      }
      try {
        const response = await service.registerUser(request)
        console.log('[checkoutStore] registerUser response', response)
        const mainStore = useMainStore()
        mainStore.setAuthToken(response.Data.SessionHandle)
        this.isAuthenticated = true
        if (response.Data.Principal) {
          this.userInfo = buildUserInfo(response.Data.Principal)
          console.log('[checkoutStore] User registered and logged in', this.userInfo)
        }
        return { success: true }
      } catch (error: any) {
        console.error('[checkoutStore] submitRegister failed', error)
        const status = error?.response?.status
        if (status === 409) {
          this.accountFormTab = AccountFormTab.LOGIN
          this.loginAlertType = LoginAlertType.WARNING
          return {
            success: false,
            message: 'That email is already registered — please log in.',
            switched: true
          }
        }
        this.loginAlertType = LoginAlertType.ERROR
        return { success: false, message: (error as Error).message }
      }
    },
    async submitLogin(): Promise<{
      success: boolean
      message?: string
      switched?: boolean
    }> {
      const service = new Sunny16CadyAccountService()
      const request: LoginRequest = {
        Email: this.login.emailOrUsername,
        Password: this.login.password
      }
      try {
        const response = await service.loginUser(request)
        console.log('[checkoutStore] loginUser response', response)
        const mainStore = useMainStore()
        mainStore.setAuthToken(response.Data.SessionHandle)
        this.isAuthenticated = true
        this.loginAlertType = LoginAlertType.NONE
        this.userInfo = buildUserInfo(response.Data.Principal)
        console.log('[checkoutStore] User logged in', this.userInfo)
        return { success: true }
      } catch (error: any) {
        console.error('[checkoutStore] submitLogin failed', error)
        this.loginAlertType = LoginAlertType.ERROR
        return { success: false, message: (error as Error).message }
      }
    },
    // --- Student assignment ---
    /** Clear the assigned student so the user is forced back through the student picker. */
    resetStudentAssignment() {
      this.studentFlowState = StudentFlowState.SEARCHING
      this.selectedStudent = null
      this.studentSource = null
    },
    // --- Checkout acknowledgments ---
    /** Clear the buyer's confirmation-step acknowledgments (relationship to student + terms acceptance). */
    resetCheckoutAcknowledgments() {
      this.relationship = ''
      this.termsAgreed = false
    },
    // --- Bulk reset ---
    /** Reset all checkout state to initial values. Called when starting a new booking. */
    resetCheckout() {
      this.accountFormTab = AccountFormTab.CREATE
      this.accountFormState = AccountFormState.FORM
      this.loginAlertType = LoginAlertType.NONE
      this.createAccount = defaultCreateAccountFields()
      this.login = defaultLoginFields()
      this.resetPassword = defaultResetPasswordFields()
      this.isAuthenticated = false
      this.userInfo = null
      this.resetStudentAssignment()
      this.resetCheckoutAcknowledgments()
    }
  },
  persist: true
})
